import React, { useState, useEffect, useCallback } from 'react';
import { Gift, RefreshCw, Trophy, Download, Search, MessageSquare, Check, Send } from 'lucide-react';
import { supabase } from '../../supabase';

function formatPhone(e164) {
  const d = e164.replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return e164;
}

// iOS wants "sms:+1XXX&body=...", Android/others want "sms:+1XXX?body=..."
const SMS_BODY_DELIM = /iPhone|iPad|iPod/.test(navigator.userAgent) ? '&' : '?';

function raffleSmsMessage(name) {
  const firstName = (name || '').trim().split(' ')[0] || 'there';
  return `Hey ${firstName}! 🦋 You're officially entered in the Monarch Detailing raffle for a free Alchemist Reset detail (valued at $200+). Check your email for the details + your personal referral link for bonus entries. Good luck! — Akeem\n\nDetailed With Precision, Delivered with Pride`;
}

function raffleSmsHref(entry) {
  const body = encodeURIComponent(raffleSmsMessage(entry.name));
  return `sms:${entry.phone}${SMS_BODY_DELIM}body=${body}`;
}

export default function Raffle() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [winner, setWinner] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('raffle_entries')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error) setEntries(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Optimistic — there's no way to know a sms: link actually got sent, only
  // that the admin clicked to open it, so we mark it texted right away and
  // let the checkbox correct it if it was a misclick.
  async function setTexted(id, value) {
    setEntries(prev => prev.map(e => (e.id === id ? { ...e, manually_texted: value } : e)));
    await supabase.from('raffle_entries').update({ manually_texted: value }).eq('id', id);
  }

  const officialWinner = entries.find(e => e.is_winner) || null;
  const nonWinners = entries.filter(e => !e.is_winner);
  const untextedLosers = nonWinners.filter(e => !e.consolation_texted);

  async function notifyLosers() {
    if (untextedLosers.length === 0) return;
    if (!window.confirm(`Text ${untextedLosers.length} entrant${untextedLosers.length === 1 ? '' : 's'} that they didn't win, with 15% off their first detail?`)) return;
    setNotifying(true);
    setNotifyResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/raffle-enter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ action: 'notify-losers' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      setNotifyResult(data);
      load();
    } catch (err) {
      setNotifyResult({ error: err.message });
    } finally {
      setNotifying(false);
    }
  }

  // Locks in the FINAL result the public wheel page reveals to everyone —
  // separate from the casual re-spinnable pickWinner() above, which is
  // just a local preview until this is called.
  async function lockInWinner() {
    if (!winner) return;
    if (officialWinner?.id === winner.id) return;
    const msg = officialWinner
      ? `${officialWinner.name} is already locked in as the official winner. Replace with ${winner.name}? This changes what the public results page shows.`
      : `Lock in ${winner.name} as the FINAL official winner? This is what the public results page will show to everyone.`;
    if (!window.confirm(msg)) return;

    await supabase.from('raffle_entries').update({ is_winner: false, drawn_at: null }).eq('is_winner', true);
    const { error } = await supabase
      .from('raffle_entries')
      .update({ is_winner: true, drawn_at: new Date().toISOString() })
      .eq('id', winner.id);
    if (!error) load();
  }

  const filtered = entries.filter(e =>
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    e.phone.includes(search) ||
    e.email.toLowerCase().includes(search.toLowerCase())
  );

  function totalEntries(e) {
    return 1 + (e.bonus_entries || 0);
  }

  function pickWinner() {
    if (entries.length === 0) return;
    // Weighted pool: each entry appears once per total entry (base + bonus referrals)
    const pool = entries.flatMap(e => Array(totalEntries(e)).fill(e));
    setSpinning(true);
    setWinner(null);
    let count = 0;
    const interval = setInterval(() => {
      const random = pool[Math.floor(Math.random() * pool.length)];
      setWinner(random);
      count++;
      if (count >= 20) {
        clearInterval(interval);
        setSpinning(false);
      }
    }, 80);
  }

  function exportCSV() {
    const header = 'Name,Phone,Email,SMS Consent,Consent Timestamp,Referred By,Bonus Entries,Total Entries,Entered At\n';
    const rows = entries.map(e =>
      `"${e.name}","${formatPhone(e.phone)}","${e.email}","${e.sms_consent ? 'Yes' : 'No'}","${e.consent_timestamp || ''}","${e.referred_by_name || ''}","${e.bonus_entries || 0}","${totalEntries(e)}","${new Date(e.created_at).toLocaleString()}"`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `raffle-entries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#68caf1]/20 flex items-center justify-center">
            <Gift className="w-5 h-5 text-[#68caf1]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#111827]">Raffle Entries</h1>
            <p className="text-sm text-[#6B7280]">
              {entries.length} total entries
              {entries.length > 0 && (
                <> · {entries.filter(e => !e.manually_texted).length} need a text</>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 text-sm font-semibold text-[#374151] border border-gray-200 rounded-xl px-4 py-2 hover:border-[#68caf1] transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={load}
            className="flex items-center gap-2 text-sm font-semibold text-[#374151] border border-gray-200 rounded-xl px-4 py-2 hover:border-[#68caf1] transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Pick winner */}
      <div className="bg-[#111827] rounded-2xl p-6 mb-6">
        <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-3">Pick a Winner</p>
        {officialWinner && (
          <div className="bg-[#68caf1]/10 border border-[#68caf1]/30 rounded-xl px-4 py-3 mb-4 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#68caf1] flex-shrink-0" />
            <p className="text-sm text-white"><strong>{officialWinner.name}</strong> is locked in as the official winner — this is what the public results page shows.</p>
          </div>
        )}
        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={pickWinner}
            disabled={spinning || entries.length === 0}
            className="bg-[#68caf1] text-black font-bold px-6 py-3 rounded-xl hover:bg-[#5bb8db] transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
          >
            <Trophy className="w-4 h-4" />
            {spinning ? 'Picking...' : 'Pick Random Winner'}
          </button>
          {winner && !spinning && (
            <>
              <div className="text-white">
                <p className="text-lg font-bold">{winner.name}</p>
                <p className="text-sm text-gray-400">{formatPhone(winner.phone)} · {winner.email}</p>
              </div>
              <button
                onClick={lockInWinner}
                disabled={officialWinner?.id === winner.id}
                className="bg-white text-[#111827] font-bold px-5 py-3 rounded-xl hover:bg-gray-100 transition-all active:scale-95 disabled:opacity-50"
              >
                {officialWinner?.id === winner.id ? 'This Is Locked In' : 'Lock In As Official Winner'}
              </button>
            </>
          )}
          {!winner && entries.length === 0 && (
            <p className="text-gray-400 text-sm">No entries yet.</p>
          )}
        </div>
      </div>

      {/* Notify non-winners */}
      {officialWinner && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-3">Notify Everyone Who Didn't Win</p>
          <p className="text-sm text-[#6B7280] mb-4">
            Texts every non-winning entrant that the raffle's been drawn, with 15% off their first detail and a booking link. Safe to run more than once — anyone already texted gets skipped.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <button
              onClick={notifyLosers}
              disabled={notifying || untextedLosers.length === 0}
              className="bg-[#111827] text-white font-bold px-6 py-3 rounded-xl hover:bg-black transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {notifying ? 'Sending...' : untextedLosers.length === 0 ? 'Everyone Notified' : `Text ${untextedLosers.length} Non-Winner${untextedLosers.length === 1 ? '' : 's'}`}
            </button>
            {notifyResult && (
              notifyResult.error
                ? <p className="text-sm text-red-500">{notifyResult.error}</p>
                : <p className="text-sm text-[#374151]">
                    Sent {notifyResult.sent}/{notifyResult.total}.
                    {notifyResult.failed?.length > 0 && <span className="text-red-500"> {notifyResult.failed.length} failed — check phone numbers and retry.</span>}
                  </p>
            )}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, phone, or email..."
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#68caf1] transition-colors"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">
          {search ? 'No entries match your search.' : 'No raffle entries yet.'}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7280] uppercase tracking-wide">#</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7280] uppercase tracking-wide">Name</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7280] uppercase tracking-wide">Phone</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7280] uppercase tracking-wide">Email</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7280] uppercase tracking-wide">SMS OK</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7280] uppercase tracking-wide">Texted</th>
                {officialWinner && <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7280] uppercase tracking-wide">Consolation</th>}
                <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7280] uppercase tracking-wide">Referred By</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7280] uppercase tracking-wide">Entries</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-[#6B7280] uppercase tracking-wide">Entered</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => (
                <tr
                  key={entry.id}
                  className={`border-b border-gray-50 last:border-0 ${winner?.id === entry.id ? 'bg-[#68caf1]/10' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <td className="px-4 py-3 text-[#9CA3AF] font-mono text-xs">{entries.length - entries.indexOf(entry)}</td>
                  <td className="px-4 py-3 font-semibold text-[#111827]">
                    <a
                      href={raffleSmsHref(entry)}
                      title="Text this entrant"
                      onClick={() => setTexted(entry.id, true)}
                      className="flex items-center gap-2 group hover:text-[#68caf1] transition-colors w-fit"
                    >
                      {winner?.id === entry.id && <Trophy className="w-3.5 h-3.5 text-[#68caf1] flex-shrink-0" />}
                      {entry.name}
                      <MessageSquare className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#68caf1] transition-colors flex-shrink-0" />
                    </a>
                  </td>
                  <td className="px-4 py-3 text-[#374151]">{formatPhone(entry.phone)}</td>
                  <td className="px-4 py-3 text-[#374151]">{entry.email}</td>
                  <td className="px-4 py-3">
                    {entry.sms_consent
                      ? <span className="text-xs font-semibold text-green-600 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">Yes</span>
                      : <span className="text-xs text-gray-400">No</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <label
                      title="Mark whether you've sent this entrant their text"
                      className="flex items-center justify-center w-6 h-6 rounded-md border border-gray-200 cursor-pointer hover:border-[#68caf1] transition-colors"
                      style={entry.manually_texted ? { backgroundColor: '#68caf1', borderColor: '#68caf1' } : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={!!entry.manually_texted}
                        onChange={e => setTexted(entry.id, e.target.checked)}
                        className="sr-only"
                      />
                      {entry.manually_texted && <Check className="w-4 h-4 text-black" />}
                    </label>
                  </td>
                  {officialWinner && (
                    <td className="px-4 py-3">
                      {entry.is_winner ? (
                        <span className="text-xs text-gray-300">—</span>
                      ) : entry.consolation_texted ? (
                        <span className="text-xs font-semibold text-green-600 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">Sent</span>
                      ) : (
                        <span className="text-xs text-gray-400">Pending</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-[#374151] text-xs">{entry.referred_by_name || '—'}</td>
                  <td className="px-4 py-3 text-[#374151]">
                    {totalEntries(entry)}
                    {entry.bonus_entries > 0 && <span className="text-xs text-[#68caf1] ml-1">(+{entry.bonus_entries})</span>}
                  </td>
                  <td className="px-4 py-3 text-[#9CA3AF] text-xs">
                    {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
